import React, { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { scrollToSection } from "@/storefront/scroll";

const normalize = (s) => s.trim().toLowerCase();

/** F4/L2-004: the ISP coverage check. Owner-configured served areas
 * (`storefront_config.vertical.coverage`, Website Settings) matched against a citizen-entered
 * locality or pincode — case-insensitive on either, which a plain lowercased-string compare
 * gives for free (digits have no case). Only mounted by `sectionsFor` when the business has
 * `enquiry` AND at least one configured area (registry.js), so the CTA below always has an
 * enquiry section to prefill and scroll to. */
export function CoverageCheckSection({ business }) {
  const { t, setEnquiryPrefill } = useApp();
  const coverage = business.storefront_config?.vertical?.coverage || [];
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null); // null | { covered: boolean, area: string }

  const check = () => {
    const area = query.trim();
    if (!area) return;
    const covered = coverage.some((entry) => normalize(entry) === normalize(area));
    setResult({ covered, area });
  };

  const goToEnquiry = () => {
    if (!result) return;
    setEnquiryPrefill({
      message: result.covered
        ? t("storefront.coverage.prefillCovered", { area: result.area })
        : t("storefront.coverage.prefillNotCovered", { area: result.area }),
    });
    scrollToSection("enquiry");
  };

  return (
    <div className="space-y-4 rounded-2xl border border-border p-5" data-testid="coverage-check-section">
      <div>
        <p className="font-heading text-base font-semibold">{t("storefront.coverage.title")}</p>
        <p className="text-sm text-muted-foreground">{t("storefront.coverage.intro")}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 space-y-1.5">
          <Label htmlFor="coverage-query">{t("storefront.coverage.inputLabel")}</Label>
          <Input
            id="coverage-query"
            data-testid="coverage-check-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setResult(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && check()}
            placeholder={t("storefront.coverage.inputPlaceholder")}
          />
        </div>
        <Button className="rounded-full" data-testid="coverage-check-btn" disabled={!query.trim()} onClick={check}>
          {t("storefront.coverage.checkCta")}
        </Button>
      </div>

      {result && result.covered && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[hsl(var(--primary))]/10 p-4"
          data-testid="coverage-check-result-yes"
        >
          <p className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))]">
            <CheckCircle2 size={18} /> {t("storefront.coverage.yes", { area: result.area })}
          </p>
          <Button className="rounded-full" data-testid="coverage-check-cta-yes" onClick={goToEnquiry}>
            {t("storefront.coverage.ctaNewConnection")}
          </Button>
        </div>
      )}

      {result && !result.covered && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-muted p-4"
          data-testid="coverage-check-result-no"
        >
          <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <XCircle size={18} /> {t("storefront.coverage.no", { area: result.area })}
          </p>
          <Button variant="outline" className="rounded-full" data-testid="coverage-check-cta-no" onClick={goToEnquiry}>
            {t("storefront.coverage.ctaRegisterInterest")}
          </Button>
        </div>
      )}
    </div>
  );
}
